import {
  DefaultButton,
  Icon,
  PrimaryButton,
  Shimmer,
  ShimmerElementType,
  Stack
} from "@fluentui/react";
import { useState } from "react";

import { DefaultCardTitle } from "../../DisplayContractInfo/styles";

import { PaymentSection } from "./PaymentSection";
import { ButtonsWrapper, Container, Section } from "./styles";

import { PaymentMethodType } from "@/common/types/PaymentMethod.types";
import {
  PaymentRequest,
  PaymentRequestStatus
} from "@/common/types/PaymentRequestTypes";
import { Card } from "@/components/Shared/Card";
import { useApi } from "@/core/libs/api/react-query/useApi";

interface PaymentAreaProps {
  paymentType: PaymentMethodType;
  paymentRequest: PaymentRequest;
  canShowConfirmPaymentButton: boolean;
  contractId: number;
refetchContractData?: () => Promise<void>;
}

export const PaymentArea = ({
  paymentType,
  paymentRequest,
  canShowConfirmPaymentButton,
  contractId,
  refetchContractData
}: PaymentAreaProps) => {
  const { execute } = useApi();

  const [isLoadingPayment, setIsLoadingPayment] = useState<boolean>(false);

  const hasPaymentIdentifier = Boolean(paymentRequest?.transactionId);

  // WARN: Fluxo legado para cartão sem billerMetadata, como na API.
  // Remover junto ao fluxo legado quando a query do PR libertas-api #2584 retornar 0 linhas.
  const isLegacyCreditCardPayment =
    paymentType === PaymentMethodType.CREDIT_CARD &&
    !paymentRequest.billerMetadata;
  // WARN:

  const showConfirmPaymentButton =
    canShowConfirmPaymentButton &&
    paymentRequest?.requestStatus === PaymentRequestStatus.PROCESSING &&
    // WARN: Cartão legado permite confirmação sem transactionId.
    (isLegacyCreditCardPayment || hasPaymentIdentifier);
  // WARN:

  const showPaymentArea = paymentType !== PaymentMethodType.INVOICE_DEBIT;

  const handleConfirmPayment = async () => {
    if (!showConfirmPaymentButton) return;

    setIsLoadingPayment(true);
    try {
      await execute({
        url: `contracts/${contractId}/status`,
        method: "POST",
        params: { interaction: "confirm-setup-paid" },
        showAlert: true,
        messages: {
          error: "Erro ao confirmar pagamento",
          success: "Pagamento confirmado com sucesso"
        }
      });

      await refetchContractData?.();
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const renderPaymentSection = (
    type: PaymentMethodType,
    paymentRequest: PaymentRequest
  ): JSX.Element | null => {
    // WARN: Fluxo legado para cartão sem billerMetadata.
    // Remover junto ao fluxo legado quando a query do PR libertas-api #2584 retornar 0 linhas.
    if (isLegacyCreditCardPayment) {
      const handleOpenChat = () => {
        window.fcWidget.open();
      };

      return (
        <Section>
          <h2 style={{ fontSize: "16px" }}>
            Solicite o link de pagamento via cartão de crédito em nossa central
            de atendimento.
          </h2>

          <ButtonsWrapper>
            <DefaultButton text="Abrir Chat" onClick={handleOpenChat} />
            {showConfirmPaymentButton && (
              <PrimaryButton
                text="Confirmar Pagamento"
                onClick={() => void handleConfirmPayment()}
              />
            )}
          </ButtonsWrapper>
        </Section>
      );
    }
    // WARN:

    switch (type) {
      case PaymentMethodType.PIX:
      case PaymentMethodType.CREDIT_CARD:
        return (
          <PaymentSection
            paymentType={type}
            paymentRequest={paymentRequest}
            handleConfirmPayment={() => void handleConfirmPayment()}
            showConfirmPaymentButton={showConfirmPaymentButton}
          />
        );
      default:
        return null;
    }
  };

  const loadingShimmerComponent = (
    <Container style={{ flexDirection: "column", width: "300px" }}>
      <Shimmer
        style={{ marginBottom: "1rem", width: "90%" }}
        shimmerElements={[{ type: ShimmerElementType.line, height: 250 }]}
      />
      <Shimmer
        style={{ marginBottom: "1rem", width: "80%" }}
        shimmerElements={[{ type: ShimmerElementType.line, height: 60 }]}
      />
    </Container>
  );

  if (showPaymentArea) {
    return (
      <Stack verticalFill style={{ marginTop: "10px" }}>
        <DefaultCardTitle>
          <Icon iconName="PaymentCard" style={{ marginRight: 10 }} />
          Área de Pagamento
        </DefaultCardTitle>

        <Card
          padding="1.5rem"
          borderRadius="0.75rem"
          height="100%"
          style={{ minHeight: "380px" }}
        >
          {isLoadingPayment
            ? loadingShimmerComponent
            : renderPaymentSection(paymentType, paymentRequest)}
        </Card>
      </Stack>
    );
  } else {
    return null;
  }
};
